import { Search, Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import type { SearchLocale } from "@/app/search-index";
import { getSearchSuggestions, searchSiteResources } from "@/app/search-index";

type NavItem = { href: string; label: string };
type ActionItem = { href: string; label: string; variant?: "primary" | "secondary" };

export function SearchExperience({
  locale,
  homeHref,
  searchPath,
  navItems,
  actionItems,
}: {
  locale: SearchLocale;
  homeHref: string;
  searchPath: string;
  navItems: NavItem[];
  actionItems: ActionItem[];
}) {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const results = searchSiteResources(locale, query);
  const suggestions = getSearchSuggestions(locale);
  const isEnglish = locale === "en";

  return (
    <PageShell>
      <TopNav navItems={navItems} actionItems={actionItems} homeHref={homeHref} />

      <section className="border-b-4 border-black bg-black px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="inline-flex items-center gap-2 border-4 border-white bg-[#A3E635] px-3 py-1.5">
            <span className="text-[11px] font-black uppercase tracking-[0.22em] text-black">
              {isEnglish ? "SITE SEARCH" : "站内搜索"}
            </span>
          </div>
          <h1 className="mt-6 text-4xl font-black uppercase leading-none text-white sm:text-6xl">
            {isEnglish ? "Find the right" : "快速找到"}
            <br />
            <span className="text-[#A3E635]">{isEnglish ? "protocol page" : "协议入口页"}</span>
          </h1>
          <p className="mt-6 max-w-3xl text-base font-bold leading-7 text-zinc-300 sm:text-lg">
            {isEnglish
              ? "This is a lightweight on-site search for stable CALL ANYTHING pages: docs, quick starts, glossary, compare pages, protocol examples, and current evidence pages."
              : "这是一个轻量站内搜索入口，专门帮助你在 CALL ANYTHING 的稳定页面里找定义页、Quick Start、协议示例页、对比页和当前证据页。"}
          </p>

          <form action={searchPath} method="get" className="mt-8 border-4 border-black bg-white p-3 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex flex-col gap-3 md:flex-row">
              <label className="flex min-w-0 flex-1 items-center gap-3 border-4 border-black bg-[#F8F6EF] px-4 py-3">
                <Search className="h-5 w-5 shrink-0 text-black" strokeWidth={3} />
                <input
                  type="search"
                  name="q"
                  defaultValue={query}
                  placeholder={isEnglish ? "Search Hotline, result_package, MCP, review payload..." : "搜索 Hotline、result_package、MCP、review payload..."}
                  className="w-full bg-transparent text-sm font-bold text-black outline-none placeholder:text-zinc-500"
                />
              </label>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 border-4 border-black bg-[#A3E635] px-6 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
              >
                <Search className="h-4 w-4" strokeWidth={3} />
                {isEnglish ? "Search" : "搜索"}
              </button>
            </div>
          </form>

          <div className="mt-5 flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <a
                key={suggestion}
                href={`${searchPath}?q=${encodeURIComponent(suggestion)}`}
                className="border-2 border-white/30 bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-white transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
              >
                {suggestion}
              </a>
            ))}
          </div>
        </div>
      </section>

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        {query ? (
          <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">
              {isEnglish ? "QUERY" : "当前查询"}
            </p>
            <h2 className="mt-2 text-2xl font-black text-black sm:text-3xl">{query}</h2>
            <p className="mt-3 text-sm font-bold text-zinc-700">
              {results.length > 0
                ? isEnglish
                  ? `${results.length} stable page(s) matched the query.`
                  : `共匹配到 ${results.length} 个稳定页面。`
                : isEnglish
                  ? "No direct match yet. Try a concept term, route name, or result field."
                  : "暂时没有直接匹配。可以换成概念词、路由名或结果字段名继续搜。"}
            </p>
          </div>
        ) : (
          <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center border-4 border-black bg-[#A3E635]">
                <Sparkles className="h-5 w-5 text-black" strokeWidth={3} />
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">
                  {isEnglish ? "TRY FIRST" : "建议先搜"}
                </p>
                <h2 className="mt-1 text-2xl font-black text-black sm:text-3xl">
                  {isEnglish ? "Start with a protocol noun or result field" : "先用协议名词或结果字段名"}
                </h2>
              </div>
            </div>
            <p className="mt-4 text-sm font-bold leading-7 text-zinc-700">
              {isEnglish
                ? "Good first queries are Hotline, result_package, review payload, Template Bundle, or one compare phrase such as Hotline vs MCP."
                : "最适合的首轮查询是 Hotline、result_package、review payload、Template Bundle，或者直接搜一个对比短语，比如 Hotline vs MCP。"}
            </p>
          </div>
        )}

        <div className="grid gap-5">
          {results.map((result) => (
            <a
              key={result.href}
              href={result.href}
              className="border-[6px] border-black bg-white/85 p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 sm:p-8"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="border-2 border-black bg-[#A3E635] px-2 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-black">
                  {result.kind}
                </span>
                <h3 className="text-2xl font-black uppercase text-black">{result.title}</h3>
              </div>
              <p className="mt-4 text-sm font-bold leading-7 text-zinc-800 sm:text-base">{result.summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {result.tags.map((tag) => (
                  <span
                    key={tag}
                    className="border-2 border-black/15 bg-zinc-100 px-2 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-zinc-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-xs font-black uppercase tracking-[0.18em] text-zinc-500">{result.href}</p>
            </a>
          ))}
        </div>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
